import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapContainer, TileLayer, Polyline, Circle, CircleMarker, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { todayISO, fmtDate, fmtDateTime, fmtTime } from "@/lib/utils-app";
import {
  ShieldCheck,
  HardHat,
  CalendarDays,
  MapPin,
  Activity,
  Users,
  Camera,
  Clock,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  RotateCw,
  CalendarCheck2,
  TreePine,
  Search,
} from "lucide-react";
import { StatTile, StatStrip } from "@/components/StatTile";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function currentMonthISO() {
  return todayISO().slice(0, 7);
}

function pct(n, d) {
  if (!d) return 0;
  return Math.min(100, Math.round((n / d) * 100));
}

/* --------------------------------------------------------------- DETAIL */
function DetailDrawer({ supervisorId, date, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supervisorId) {
      setData(null);
      return;
    }
    setBusy(true);
    api
      .get("/admin/supervisor-monitoring/detail", { params: { supervisor_id: supervisorId, date } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [supervisorId, date]);

  const trail = data?.gps_trail || [];
  const trailPoints = trail.map((p) => [p.lat, p.long]).filter((p) => p[0] != null && p[1] != null);
  const parksWithCoords = (data?.per_park || []).filter((p) => p.latitude != null && p.longitude != null);

  const mapCenter = useMemo(() => {
    if (trailPoints.length) return trailPoints[0];
    if (parksWithCoords.length) return [parksWithCoords[0].latitude, parksWithCoords[0].longitude];
    return [11.1271, 78.6569]; // Tamil Nadu fallback
  }, [trailPoints, parksWithCoords]);

  const sup = data?.supervisor;
  const totals = data?.totals || {};
  const coverage = pct(totals.actual || 0, totals.expected || 0);

  return (
    <Dialog open={!!supervisorId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="sup-mon-detail-dialog">
        <DialogHeader>
          <DialogTitle>
            {sup ? sup.full_name : "Supervisor"} — {fmtDate(date)}
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            {sup ? (
              <>
                <span className="font-mono">{sup.username}</span> · {sup.role === "pmc_supervisor" ? "ITCOT / PMC" : "Contractor"}
              </>
            ) : null}
          </div>
        </DialogHeader>

        {busy && (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        )}

        {data && (
          <div className="grid max-h-[75vh] gap-3 overflow-y-auto">
            {/* Summary strip */}
            <StatStrip cols={6}>
              <StatTile icon={Clock} accent="indigo" label="Start" value={data.start_time || "—"} hint="First activity" testid="sm-stat-start" />
              <StatTile icon={Clock} accent="violet" label="End" value={data.end_time || "—"} hint="Last activity" testid="sm-stat-end" />
              <StatTile icon={CalendarCheck2} accent="teal" label="Park visits" value={totals.visits ?? 0} hint={`${totals.parks_visited ?? 0} parks today`} testid="sm-stat-visits" />
              <StatTile icon={Users} accent="slate" label="Workers" value={totals.actual ?? 0} hint={`${totals.expected ?? 0} expected`} testid="sm-stat-actual" />
              <StatTile icon={AlertTriangle} accent="amber" label="Pending" value={totals.pending ?? 0} testid="sm-stat-pending" />
              <StatTile icon={MapPin} accent="emerald" label="GPS pings" value={totals.gps_pings ?? 0} hint={`${coverage}% coverage`} testid="sm-stat-pings" />
            </StatStrip>

            {/* Per-park breakdown */}
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sm-detail-parks">
                  <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700">
                    <tr className="border-b border-slate-200">
                      <th className="whitespace-nowrap px-2 py-2 text-center">Park</th>
                      <th className="whitespace-nowrap px-3 py-2 text-left">Name</th>
                      <th className="whitespace-nowrap px-2 py-2 text-center">Workers</th>
                      <th className="whitespace-nowrap px-2 py-2 text-center">Windows</th>
                      <th className="whitespace-nowrap px-2 py-2 text-center">Visits</th>
                      <th className="whitespace-nowrap px-2 py-2 text-center">Expected</th>
                      <th className="whitespace-nowrap px-2 py-2 text-center">Actual</th>
                      <th className="whitespace-nowrap px-2 py-2 text-center">Pending</th>
                      <th className="whitespace-nowrap px-2 py-2 text-center">Coverage</th>
                      <th className="whitespace-nowrap px-2 py-2 text-center">Last capture</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.per_park || []).map((p) => {
                      const cov = pct(p.actual, p.expected);
                      const barCls = cov >= 80 ? "bg-emerald-500" : cov >= 40 ? "bg-amber-500" : "bg-rose-500";
                      const rowCls = p.actual === 0
                        ? "bg-rose-50/40"
                        : p.pending > 0
                          ? "bg-amber-50/40"
                          : "";
                      return (
                        <tr key={p.park_id} className={`border-b border-slate-100 ${rowCls}`}>
                          <td className="whitespace-nowrap px-2 py-2 text-center font-mono text-xs font-semibold text-slate-800">{p.code}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-left">{p.name}</td>
                          <td className="whitespace-nowrap px-2 py-2 text-center tabular-nums">{p.workers}</td>
                          <td className="whitespace-nowrap px-2 py-2 text-center tabular-nums">{p.windows?.length ?? 0}</td>
                          <td className="whitespace-nowrap px-2 py-2 text-center">
                            {p.visits > 0 ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 tabular-nums">
                                <CalendarCheck2 className="h-3 w-3" />{p.visits}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-center tabular-nums">{p.expected}</td>
                          <td className="whitespace-nowrap px-2 py-2 text-center tabular-nums font-semibold text-slate-900">{p.actual}</td>
                          <td className="whitespace-nowrap px-2 py-2 text-center tabular-nums">
                            {p.pending > 0
                              ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800">{p.pending}</span>
                              : <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-800">0</span>}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="h-1.5 w-16 rounded-full bg-slate-200 overflow-hidden">
                                <div className={`h-full ${barCls}`} style={{ width: `${cov}%` }} />
                              </div>
                              <span className="font-mono text-[11px] tabular-nums text-slate-600">{cov}%</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-center text-xs text-slate-600">
                            {p.last_capture_at ? fmtTime(p.last_capture_at) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* GPS Trail map */}
            <Card>
              <CardContent className="p-3">
                <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-700">
                  <Activity className="h-3.5 w-3.5 text-emerald-600" /> GPS Trail
                  <span className="ml-auto text-[11px] font-normal text-slate-500">
                    {trail.length} pings · {data.selfies?.length || 0} selfies
                  </span>
                </div>
                <div className="h-[420px] w-full overflow-hidden rounded-md border" data-testid="sm-detail-map">
                  <MapContainer center={mapCenter} zoom={trailPoints.length ? 12 : 7} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
                    <TileLayer
                      attribution='&copy; OpenStreetMap contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {parksWithCoords.map((p) => (
                      <Circle
                        key={p.park_id}
                        center={[p.latitude, p.longitude]}
                        radius={p.geofence_radius_m || 500}
                        pathOptions={{ color: "#f59e0b", fillColor: "#f59e0b", fillOpacity: 0.08, weight: 1.5 }}
                      >
                        <Popup>{p.code} — {p.name}</Popup>
                      </Circle>
                    ))}
                    {trailPoints.length >= 2 && (
                      <Polyline positions={trailPoints} pathOptions={{ color: "#2563eb", weight: 3, opacity: 0.7 }} />
                    )}
                    {trail.map((p, idx) => (
                      <CircleMarker
                        key={idx}
                        center={[p.lat, p.long]}
                        radius={3}
                        pathOptions={{ color: "#1d4ed8", fillColor: "#3b82f6", fillOpacity: 0.9, weight: 1 }}
                      >
                        <Popup>{fmtDateTime(p.captured_at)}</Popup>
                      </CircleMarker>
                    ))}
                    {trailPoints.length > 0 && (
                      <Marker position={trailPoints[trailPoints.length - 1]}>
                        <Popup>Last seen<br/>{fmtDateTime(trail[trail.length - 1]?.captured_at)}</Popup>
                      </Marker>
                    )}
                  </MapContainer>
                </div>
                {trail.length === 0 && (
                  <div className="mt-2 rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">
                    No GPS pings logged on this date.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Park visits done today */}
            {(data.park_visits || []).length > 0 && (
              <Card>
                <CardContent className="p-3">
                  <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-700">
                    <CalendarCheck2 className="h-3.5 w-3.5 text-teal-600" /> Park visits today
                    <span className="ml-auto text-[11px] font-normal text-slate-500">
                      {data.park_visits.length} visit{data.park_visits.length === 1 ? "" : "s"} · {totals.parks_visited ?? 0} park{(totals.parks_visited ?? 0) === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="sm-detail-visits">
                    {data.park_visits.map((v) => {
                      const qualCls =
                        v.work_quality === "excellent"
                          ? "bg-emerald-600 hover:bg-emerald-600 text-white"
                          : v.work_quality === "good"
                          ? "bg-sky-600 hover:bg-sky-600 text-white"
                          : v.work_quality === "average"
                          ? "bg-amber-500 hover:bg-amber-500 text-white"
                          : "bg-rose-600 hover:bg-rose-600 text-white";
                      return (
                        <div key={v.id} className="overflow-hidden rounded-md border" data-testid={`sm-visit-${v.id}`}>
                          <div className="grid grid-cols-2 gap-px bg-slate-100">
                            <div className="relative aspect-square overflow-hidden bg-slate-200">
                              {v.selfie_b64 ? (
                                <img
                                  src={v.selfie_b64?.startsWith("data:") ? v.selfie_b64 : `data:image/jpeg;base64,${v.selfie_b64}`}
                                  alt="Selfie"
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">No selfie</div>
                              )}
                              <Badge className="absolute left-1 top-1 bg-slate-900/80 text-white">Selfie</Badge>
                            </div>
                            <div className="relative aspect-square overflow-hidden bg-slate-200">
                              {v.work_photo_b64 ? (
                                <img
                                  src={v.work_photo_b64?.startsWith("data:") ? v.work_photo_b64 : `data:image/jpeg;base64,${v.work_photo_b64}`}
                                  alt="Work"
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">No work photo</div>
                              )}
                              <Badge className="absolute left-1 top-1 bg-slate-900/80 text-white">Work</Badge>
                            </div>
                          </div>
                          <div className="space-y-1 p-2 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900 truncate">{v.park_name || "—"}</span>
                              <span className="ml-auto font-mono text-[10px] text-slate-500">{fmtTime(v.visited_at)}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-1">
                              {v.work_quality && (
                                <Badge className={`${qualCls} capitalize text-[10px]`}>{v.work_quality}</Badge>
                              )}
                              {v.inside_geofence === false && (
                                <Badge variant="secondary" className="bg-rose-100 text-rose-800 text-[10px]">
                                  <MapPin className="mr-0.5 h-3 w-3" /> Outside geofence
                                </Badge>
                              )}
                              {v.inside_geofence === true && (
                                <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 text-[10px]">
                                  <MapPin className="mr-0.5 h-3 w-3" /> Inside geofence
                                </Badge>
                              )}
                            </div>
                            {v.notes && (
                              <div className="text-[11px] text-slate-700 line-clamp-2"><span className="font-medium">Notes:</span> {v.notes}</div>
                            )}
                            {v.issues && (
                              <div className="text-[11px] text-rose-700 line-clamp-2"><span className="font-medium">Issues:</span> {v.issues}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Selfies */}
            {(data.selfies || []).length > 0 && (
              <Card>
                <CardContent className="p-3">
                  <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-700">
                    <Camera className="h-3.5 w-3.5 text-amber-600" /> Supervisor selfies
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {data.selfies.map((s) => (
                      <div key={s.id} className="overflow-hidden rounded-md border">
                        <div className="aspect-square">
                          <img
                            src={s.photo_b64?.startsWith("data:") ? s.photo_b64 : `data:image/jpeg;base64,${s.photo_b64}`}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="p-1.5 text-[11px]">
                          <div className="font-medium truncate">{s.park_name || "—"}</div>
                          <div className="text-muted-foreground">{s.window_label || "Open"} · {fmtTime(s.created_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------- DAILY MONITOR TAB */
function DailyMonitorTab({ role, accent, icon: Icon }) {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drillId, setDrillId] = useState(null);
  const [search, setSearch] = useState("");

  const reload = () => {
    setBusy(true);
    api
      .get("/admin/supervisor-monitoring", { params: { role, date } })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [] }))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [role, date]);

  const rows = (data?.rows || []).filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.full_name || "").toLowerCase().includes(q) ||
      (r.username || "").toLowerCase().includes(q) ||
      (r.assigned_parks || []).some((p) => (p.code || "").toLowerCase().includes(q) || (p.name || "").toLowerCase().includes(q))
    );
  });

  const totals = rows.reduce(
    (a, r) => ({
      supervisors: a.supervisors + 1,
      active: a.active + (r.actual_captures > 0 || r.gps_ping_count > 0 || r.visits_count > 0 ? 1 : 0),
      expected: a.expected + (r.expected_captures || 0),
      actual: a.actual + (r.actual_captures || 0),
      pending: a.pending + (r.pending_count || 0),
      visits: a.visits + (r.visits_count || 0),
    }),
    { supervisors: 0, active: 0, expected: 0, actual: 0, pending: 0, visits: 0 },
  );
  const totalCoverage = pct(totals.actual, totals.expected);

  return (
    <div className="flex flex-col gap-4">
      {/* Filter bar */}
      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-12">
          <div className="md:col-span-4 flex flex-col gap-1.5">
            <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <CalendarDays className="mr-1 inline h-3 w-3" /> Date
            </Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sm-date" className="h-10" />
          </div>
          <div className="md:col-span-6 flex flex-col gap-1.5">
            <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <Search className="mr-1 inline h-3 w-3" /> Search
            </Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, ID, or park"
              data-testid="sm-search"
              className="h-10"
            />
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button variant="outline" onClick={reload} disabled={busy} className="h-10 w-full" data-testid="sm-refresh">
              <RotateCw className={`mr-1 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Strip */}
      <StatStrip cols={6}>
        <StatTile icon={Icon} accent={accent} label="Supervisors" value={totals.supervisors} testid="sm-strip-total" />
        <StatTile icon={Activity} accent="emerald" label="Active today" value={totals.active} hint={`${pct(totals.active, totals.supervisors)}% engagement`} testid="sm-strip-active" />
        <StatTile icon={CalendarCheck2} accent="teal" label="Park visits" value={totals.visits} testid="sm-strip-visits" />
        <StatTile icon={Users} accent="slate" label="Captures" value={totals.actual} hint={`of ${totals.expected} expected`} testid="sm-strip-captures" />
        <StatTile icon={CheckCircle2} accent="indigo" label="Coverage" value={`${totalCoverage}%`} testid="sm-strip-coverage" />
        <StatTile icon={AlertTriangle} accent="amber" label="Pending" value={totals.pending} testid="sm-strip-pending" />
      </StatStrip>

      {/* Table */}
      {busy ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <Users className="h-8 w-8 opacity-40" />
            <div>No supervisors match the current filter.</div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sm-rows">
              <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700">
                <tr className="border-b border-slate-200">
                  <th className="whitespace-nowrap px-2 py-2 text-center">#</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">ID</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left">Supervisor</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">Parks</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">Start</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">End</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">Visits</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">Expected</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">Actual</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">Pending</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">Coverage</th>
                  <th className="whitespace-nowrap px-3 py-2 text-left">Per-park</th>
                  <th className="whitespace-nowrap px-2 py-2 text-center">Open</th>
                </tr>
              </thead>
              <tbody>
                <TooltipProvider delayDuration={120}>
                  {rows.map((r, idx) => {
                    const cov = pct(r.actual_captures, r.expected_captures);
                    const barCls = cov >= 80 ? "bg-emerald-500" : cov >= 40 ? "bg-amber-500" : "bg-rose-500";
                    const rowCls = r.actual_captures === 0 && r.gps_ping_count === 0
                      ? "bg-rose-50/40"
                      : r.pending_count > 0
                        ? "bg-amber-50/30"
                        : "hover:bg-slate-50";
                    return (
                      <tr
                        key={r.supervisor_id}
                        className={`border-b border-slate-100 cursor-pointer ${rowCls}`}
                        onClick={() => setDrillId(r.supervisor_id)}
                        data-testid={`sm-row-${r.username}`}
                      >
                        <td className="whitespace-nowrap px-2 py-2 text-center font-mono text-xs text-slate-500 tabular-nums">{idx + 1}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-center"><span className="font-mono text-xs font-semibold text-slate-800">{r.username}</span></td>
                        <td className="whitespace-nowrap px-3 py-2 text-left font-medium text-slate-900">{r.full_name}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700 tabular-nums">
                                <TreePine className="h-3 w-3" /> {r.assigned_parks_count}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              {(r.assigned_parks || []).length === 0 ? "No parks assigned" : (
                                <div className="space-y-0.5 text-xs">
                                  {(r.assigned_parks || []).map((p) => (
                                    <div key={p.id}><span className="font-mono">{p.code}</span> — {p.name}</div>
                                  ))}
                                </div>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-center font-mono text-xs tabular-nums">{r.start_time || "—"}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-center font-mono text-xs tabular-nums">{r.end_time || "—"}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-center">
                          {r.visits_count > 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-800 tabular-nums">
                              <CalendarCheck2 className="h-3 w-3" />{r.visits_count}
                              {r.parks_visited_count != null ? <span className="text-[10px] font-normal opacity-80">· {r.parks_visited_count} parks</span> : null}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-center tabular-nums">{r.expected_captures}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-center font-semibold tabular-nums text-slate-900">{r.actual_captures}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-center tabular-nums">
                          {r.pending_count > 0
                            ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800">{r.pending_count}</span>
                            : <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-800">0</span>}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="h-1.5 w-16 rounded-full bg-slate-200 overflow-hidden">
                              <div className={`h-full ${barCls}`} style={{ width: `${cov}%` }} />
                            </div>
                            <span className="font-mono text-[11px] tabular-nums text-slate-600">{cov}%</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-left">
                          <div className="flex max-w-[420px] flex-wrap gap-1">
                            {(r.per_park || []).slice(0, 6).map((p) => {
                              const c = pct(p.actual, p.expected);
                              const cls = p.actual === 0
                                ? "bg-rose-100 text-rose-800 ring-1 ring-rose-200"
                                : c >= 80
                                  ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200"
                                  : "bg-amber-100 text-amber-800 ring-1 ring-amber-200";
                              return (
                                <Tooltip key={p.park_id}>
                                  <TooltipTrigger asChild>
                                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${cls}`}>
                                      <span className="font-mono font-semibold">{p.code}</span>
                                      <span className="max-w-[110px] truncate">{p.name}</span>
                                      <span className="opacity-80">· {p.actual}/{p.expected}</span>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <div className="text-xs">
                                      <div className="font-medium">{p.code} — {p.name}</div>
                                      <div>{p.workers} workers · {p.windows} windows</div>
                                      <div>{p.actual} / {p.expected} captured · {p.pending} pending</div>
                                      {p.visits ? <div>{p.visits} park visit{p.visits === 1 ? "" : "s"}</div> : null}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })}
                            {(r.per_park || []).length > 6 && (
                              <span className="text-[10px] text-slate-500">+{r.per_park.length - 6} more</span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-center"><ChevronRight className="mx-auto h-4 w-4 text-muted-foreground" /></td>
                      </tr>
                    );
                  })}
                </TooltipProvider>
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-3 border-t bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
            <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-emerald-200" />≥ 80% coverage</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-amber-200" />Partial / pending</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-rose-200" />Inactive / no captures</span>
            <span className="ml-auto text-slate-500">Click any row for park-wise detail &amp; GPS trail</span>
          </div>
        </Card>
      )}

      <DetailDrawer supervisorId={drillId} date={date} onClose={() => setDrillId(null)} />
    </div>
  );
}

/* ----------------------------------------- PARK VISITS CALENDAR DIALOG */
function ParkVisitsCalendarDialog({ open, supervisorId, supervisorName, username, month, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    if (!open || !supervisorId) {
      setData(null);
      setSelectedDay(null);
      return;
    }
    setBusy(true);
    api
      .get("/admin/supervisor-monitoring/park-visits/calendar", { params: { supervisor_id: supervisorId, month } })
      .then((r) => {
        setData(r.data);
        // Pre-select the first day that has a visit, else today, else 1st
        const firstVisit = (r.data.days || []).find((d) => (d.visits || []).length > 0);
        const today = (r.data.days || []).find((d) => d.is_today);
        setSelectedDay(firstVisit?.date || today?.date || r.data.days?.[0]?.date || null);
      })
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [open, supervisorId, month]);

  const days = useMemo(() => data?.days || [], [data]);
  const assignedParks = useMemo(() => data?.assigned_parks || [], [data]);
  const totalVisits = days.reduce((a, d) => a + d.visits.length, 0);
  const daysVisited = days.filter((d) => d.visits.length > 0).length;
  const monthLabel = data?.month
    ? new Date(`${data.month}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" })
    : "";

  // First-day-of-month weekday offset (Mon=0, Sun=6) for grid alignment
  const firstWeekday = days[0]?.weekday ?? 0;

  // Each assigned park gets its own colour. Up to 6 parks – palette wraps if more.
  const PARK_PALETTE = [
    { bg: "bg-sky-400",     light: "bg-sky-100",     ring: "ring-sky-400",     text: "text-sky-900",     dot: "bg-sky-500" },
    { bg: "bg-emerald-400", light: "bg-emerald-100", ring: "ring-emerald-400", text: "text-emerald-900", dot: "bg-emerald-500" },
    { bg: "bg-violet-400",  light: "bg-violet-100",  ring: "ring-violet-400",  text: "text-violet-900",  dot: "bg-violet-500" },
    { bg: "bg-rose-400",    light: "bg-rose-100",    ring: "ring-rose-400",    text: "text-rose-900",    dot: "bg-rose-500" },
    { bg: "bg-teal-400",    light: "bg-teal-100",    ring: "ring-teal-400",    text: "text-teal-900",    dot: "bg-teal-500" },
    { bg: "bg-orange-400",  light: "bg-orange-100",  ring: "ring-orange-400",  text: "text-orange-900",  dot: "bg-orange-500" },
  ];
  const parkColor = useMemo(() => {
    const map = {};
    assignedParks.forEach((p, i) => { map[p.id] = PARK_PALETTE[i % PARK_PALETTE.length]; });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedParks.map((p) => p.id).join("|")]);

  // Park-wise monthly totals (for legend)
  const monthlyByPark = useMemo(() => {
    const out = {};
    assignedParks.forEach((p) => { out[p.id] = 0; });
    days.forEach((d) => d.visits.forEach((v) => { out[v.park_id] = (out[v.park_id] || 0) + 1; }));
    return out;
  }, [assignedParks, days]);

  // Build week rows (so we can render a "Week" summary column at the right edge)
  const weeks = useMemo(() => {
    if (!days.length) return [];
    const cells = [
      ...Array.from({ length: firstWeekday }, () => null),
      ...days,
    ];
    const w = [];
    for (let i = 0; i < cells.length; i += 7) w.push(cells.slice(i, i + 7));
    // pad last week to 7
    if (w.length) {
      const last = w[w.length - 1];
      while (last.length < 7) last.push(null);
    }
    return w;
  }, [days, firstWeekday]);

  // For each week, count visits per park
  const weekSummaries = useMemo(() => {
    return weeks.map((week) => {
      const byPark = {};
      let total = 0;
      week.forEach((cell) => {
        if (!cell) return;
        cell.visits.forEach((v) => {
          byPark[v.park_id] = (byPark[v.park_id] || 0) + 1;
          total += 1;
        });
      });
      return { byPark, total };
    });
  }, [weeks]);

  const selectedDayObj = days.find((d) => d.date === selectedDay);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="sm-pv-cal-dialog">
        <DialogHeader>
          <DialogTitle>{supervisorName} — Visits in {monthLabel}</DialogTitle>
          <div className="text-xs text-muted-foreground">
            <span className="font-mono">@{username}</span> · {daysVisited} day{daysVisited === 1 ? "" : "s"} active · {totalVisits} visit{totalVisits === 1 ? "" : "s"} total
          </div>
        </DialogHeader>

        {busy ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : !data ? null : (
          <div className="grid gap-4 max-h-[75vh] overflow-y-auto md:grid-cols-[auto,1fr]">
            {/* Calendar grid */}
            <div className="rounded-md border bg-white p-3">
              <div className="mb-2 grid grid-cols-[repeat(7,minmax(0,1fr))_auto] gap-1 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d}>{d}</div>)}
                <div className="min-w-[112px] border-l border-slate-200 pl-2 text-slate-600">Wk · per park</div>
              </div>

              {weeks.map((week, wi) => (
                <div key={wi} className="mb-1 grid grid-cols-[repeat(7,minmax(0,1fr))_auto] gap-1">
                  {week.map((d, di) => {
                    if (!d) {
                      return <div key={`pad-${wi}-${di}`} className="h-14" />;
                    }
                    const has = d.visits.length > 0;
                    const isSelected = d.date === selectedDay;
                    const dateNum = parseInt(d.date.slice(-2), 10);
                    // Pick the colour of the FIRST park visited on this day for the
                    // background; show small dots for additional parks visited.
                    const primaryColor = has ? parkColor[d.visits[0].park_id] : null;
                    const otherColors = has
                      ? Array.from(new Set(d.visits.slice(1).map((v) => v.park_id))).map((pid) => parkColor[pid]).filter(Boolean)
                      : [];
                    const baseCls = "relative h-14 rounded-md border text-left text-[11px] flex flex-col items-stretch justify-between p-1.5 transition-colors";
                    let stateCls;
                    if (has && primaryColor) {
                      stateCls = `${primaryColor.light} ${primaryColor.text} border-transparent cursor-pointer hover:opacity-90`;
                      if (isSelected) stateCls += ` ring-2 ${primaryColor.ring}`;
                    } else if (d.is_future) {
                      stateCls = "bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed";
                    } else {
                      stateCls = "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed opacity-70";
                    }
                    return (
                      <button
                        key={d.date}
                        type="button"
                        disabled={!has}
                        onClick={() => has && setSelectedDay(d.date)}
                        className={`${baseCls} ${stateCls}`}
                        data-testid={`sm-pv-cal-day-${d.date}`}
                        title={has
                          ? d.visits.map((v) => `${v.park_code} ${v.park_name}`).join(", ")
                          : d.is_future ? "Future" : "No visit"}
                      >
                        <div className="flex items-start justify-between">
                          <span className="font-mono font-semibold tabular-nums">{dateNum}</span>
                          {has && d.visits.length > 1 && (
                            <span className="rounded-full bg-white/70 px-1 text-[9px] font-bold">{d.visits.length}</span>
                          )}
                        </div>
                        {has && (
                          <div className="flex flex-wrap items-center gap-0.5">
                            <span className={`inline-block h-1.5 w-1.5 rounded-full ${primaryColor.dot}`} />
                            {otherColors.map((c, i) => (
                              <span key={i} className={`inline-block h-1.5 w-1.5 rounded-full ${c.dot}`} />
                            ))}
                          </div>
                        )}
                        {d.is_today && (
                          <span className="absolute -top-1 -right-1 inline-block h-2 w-2 rounded-full bg-sky-500 ring-2 ring-white" />
                        )}
                      </button>
                    );
                  })}
                  {/* Weekly summary column */}
                  <div className="flex h-14 min-w-[112px] flex-col gap-0.5 border-l border-slate-200 pl-2 text-[10px]">
                    {weekSummaries[wi].total === 0 ? (
                      <span className="my-auto text-slate-400">—</span>
                    ) : (
                      assignedParks
                        .filter((p) => weekSummaries[wi].byPark[p.id])
                        .map((p) => {
                          const col = parkColor[p.id];
                          return (
                            <div key={p.id} className="flex items-center gap-1 leading-tight">
                              <span className={`inline-block h-2 w-2 rounded-full ${col?.dot || "bg-slate-400"}`} />
                              <span className="font-mono font-semibold text-slate-700">{p.code}</span>
                              <span className="ml-auto tabular-nums font-semibold text-slate-900">{weekSummaries[wi].byPark[p.id]}</span>
                            </div>
                          );
                        })
                    )}
                  </div>
                </div>
              ))}

              {/* Legend: park colours + monthly totals */}
              <div className="mt-3 border-t border-slate-200 pt-2">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Park legend (monthly totals)</div>
                <div className="flex flex-wrap gap-2">
                  {assignedParks.map((p) => {
                    const col = parkColor[p.id];
                    const count = monthlyByPark[p.id] || 0;
                    return (
                      <span
                        key={p.id}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${col?.light || "bg-slate-100"} ${col?.text || "text-slate-700"}`}
                      >
                        <span className={`inline-block h-2 w-2 rounded-full ${col?.dot || "bg-slate-400"}`} />
                        <span className="font-mono font-semibold">{p.code}</span>
                        <span>{p.name}</span>
                        <span className="ml-1 rounded-full bg-white/70 px-1.5 text-[10px] font-semibold tabular-nums">{count}</span>
                      </span>
                    );
                  })}
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-slate-600">
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded bg-slate-100 ring-1 ring-slate-300" />No visit</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-sky-500" />Today</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded bg-amber-100 ring-1 ring-amber-300" />Selected day (matches park colour)</span>
                  <span className="ml-auto text-slate-500">Dots inside a cell = parks visited that day</span>
                </div>
              </div>
            </div>

            {/* Day detail panel */}
            <div className="rounded-md border bg-white p-3">
              {!selectedDayObj || selectedDayObj.visits.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
                  <CalendarCheck2 className="h-8 w-8 opacity-40" />
                  <div>{selectedDayObj ? "No visit on this day" : "Select a highlighted day to see visit details"}</div>
                </div>
              ) : (
                <>
                  <div className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-700">
                    <CalendarCheck2 className="h-3.5 w-3.5 text-amber-600" />
                    <span>{fmtDate(selectedDayObj.date)}</span>
                    <span className="ml-auto text-[11px] font-normal text-slate-500">
                      {selectedDayObj.visits.length} visit{selectedDayObj.visits.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {selectedDayObj.visits.map((v, idx) => {
                      const qualCls =
                        v.work_quality === "excellent" ? "bg-emerald-600 text-white"
                          : v.work_quality === "good" ? "bg-sky-600 text-white"
                          : v.work_quality === "average" ? "bg-amber-500 text-white"
                          : v.work_quality ? "bg-rose-600 text-white" : "bg-slate-300 text-slate-700";
                      return (
                        <div key={idx} className="overflow-hidden rounded-md border" data-testid={`sm-pv-cal-visit-${idx}`}>
                          <div className="grid grid-cols-2 gap-px bg-slate-100">
                            <div className="aspect-square overflow-hidden bg-slate-200">
                              {v.selfie_b64 ? (
                                <img src={v.selfie_b64?.startsWith("data:") ? v.selfie_b64 : `data:image/jpeg;base64,${v.selfie_b64}`} alt="Selfie" className="h-full w-full object-cover" />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">No selfie</div>
                              )}
                            </div>
                            <div className="aspect-square overflow-hidden bg-slate-200">
                              {v.work_photo_b64 ? (
                                <img src={v.work_photo_b64?.startsWith("data:") ? v.work_photo_b64 : `data:image/jpeg;base64,${v.work_photo_b64}`} alt="Work" className="h-full w-full object-cover" />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">No work photo</div>
                              )}
                            </div>
                          </div>
                          <div className="space-y-1 p-2 text-xs">
                            <div className="flex items-center gap-2">
                              <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${parkColor[v.park_id]?.dot || "bg-slate-400"}`} />
                              <span className="font-mono font-semibold text-amber-700">{v.park_code}</span>
                              <span className="font-medium text-slate-900 truncate">{v.park_name}</span>
                              <span className="ml-auto font-mono text-[10px] text-slate-500">{fmtTime(v.visited_at)}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-1">
                              {v.work_quality && <Badge className={`${qualCls} capitalize text-[10px] hover:opacity-90`}>{v.work_quality}</Badge>}
                              {v.inside_geofence === false && <Badge className="bg-rose-100 text-rose-800 text-[10px] hover:bg-rose-100">Outside geofence</Badge>}
                              {v.inside_geofence === true && <Badge className="bg-emerald-100 text-emerald-800 text-[10px] hover:bg-emerald-100">Inside geofence</Badge>}
                            </div>
                            {v.notes && <div className="text-[11px] text-slate-700"><span className="font-medium">Notes:</span> {v.notes}</div>}
                            {v.issues && <div className="text-[11px] text-rose-700"><span className="font-medium">Issues:</span> {v.issues}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------- MONTHLY PARK VISITS TAB */
function MonthlyParkVisitsTab() {
  const [month, setMonth] = useState(currentMonthISO());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [calOpen, setCalOpen] = useState(null); // {supervisor_id, full_name}
  const [slaDays, setSlaDays] = useState(7);

  const reload = () => {
    setBusy(true);
    api
      .get("/admin/supervisor-monitoring/park-visits", { params: { month, sla_days: slaDays } })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [] }))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [month, slaDays]);

  const rows = (data?.rows || []).filter((r) => {
    if (onlyOverdue && r.overdue_count === 0) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.full_name || "").toLowerCase().includes(q) ||
      (r.username || "").toLowerCase().includes(q) ||
      (r.parks || []).some((p) => (p.code || "").toLowerCase().includes(q) || (p.name || "").toLowerCase().includes(q))
    );
  });

  const totals = rows.reduce(
    (a, r) => ({
      supervisors: a.supervisors + 1,
      compliant: a.compliant + (r.overdue_count === 0 ? 1 : 0),
      overdueSup: a.overdueSup + (r.overdue_count > 0 ? 1 : 0),
      visited: a.visited + (r.visited_in_month || 0),
      assigned: a.assigned + (r.assigned_count || 0),
    }),
    { supervisors: 0, compliant: 0, overdueSup: 0, visited: 0, assigned: 0 },
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-12">
          <div className="md:col-span-4 flex flex-col gap-1.5">
            <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <CalendarCheck2 className="mr-1 inline h-3 w-3" /> Month
            </Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="sm-month" className="h-10" />
          </div>
          <div className="md:col-span-3 flex flex-col gap-1.5">
            <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <ShieldCheck className="mr-1 inline h-3 w-3" /> Visit SLA
            </Label>
            <Select value={String(slaDays)} onValueChange={(v) => setSlaDays(parseInt(v, 10))}>
              <SelectTrigger className="h-10" data-testid="sm-pv-sla"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">Every 3 days</SelectItem>
                <SelectItem value="5">Every 5 days</SelectItem>
                <SelectItem value="7">Every week (default)</SelectItem>
                <SelectItem value="14">Every 2 weeks</SelectItem>
                <SelectItem value="30">Once a month</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex flex-col gap-1.5">
            <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <Search className="mr-1 inline h-3 w-3" /> Search
            </Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, ID, or park"
              data-testid="sm-pv-search"
              className="h-10"
            />
          </div>
          <div className="md:col-span-3 flex items-end gap-2">
            <Button
              variant={onlyOverdue ? "default" : "outline"}
              onClick={() => setOnlyOverdue((v) => !v)}
              className="h-10 flex-1"
              data-testid="sm-pv-only-overdue"
            >
              <AlertTriangle className="mr-1 h-3.5 w-3.5" />
              {onlyOverdue ? "Showing overdue" : "Show overdue only"}
            </Button>
            <Button variant="outline" onClick={reload} disabled={busy} className="h-10" data-testid="sm-pv-refresh">
              <RotateCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      <StatStrip cols={4}>
        <StatTile icon={ShieldCheck} accent="sky" label="ITCOT Supervisors" value={totals.supervisors} testid="sm-pv-strip-sup" />
        <StatTile icon={CheckCircle2} accent="emerald" label={`Compliant (≤ ${slaDays}d)`} value={totals.compliant} hint={`${pct(totals.compliant, totals.supervisors)}%`} testid="sm-pv-strip-comp" />
        <StatTile icon={AlertTriangle} accent="rose" label="Overdue supervisors" value={totals.overdueSup} testid="sm-pv-strip-od" />
        <StatTile icon={TreePine} accent="amber" label="Park visits this month" value={totals.visited} hint={`of ${totals.assigned} assignments`} testid="sm-pv-strip-vis" />
      </StatStrip>

      {busy ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <Users className="h-8 w-8 opacity-40" />
            <div>No ITCOT supervisors found{onlyOverdue ? " with overdue parks." : "."}</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="sm-pv-cards">
          {rows.map((r) => {
            const compliant = r.overdue_count === 0 && r.assigned_count > 0;
            const ringCls = r.overdue_count > 0
              ? "ring-2 ring-rose-300 bg-rose-50/30"
              : compliant
                ? "ring-1 ring-emerald-200 bg-emerald-50/30"
                : "ring-1 ring-slate-200";
            return (
              <Card key={r.supervisor_id} className={`${ringCls} cursor-pointer transition-colors hover:bg-amber-50/40`} data-testid={`sm-pv-card-${r.username}`} onClick={() => setCalOpen({ supervisor_id: r.supervisor_id, full_name: r.full_name, username: r.username })}>
                <CardContent className="flex flex-col gap-3 p-3">
                  <div className="flex items-center gap-2">
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-sky-100 text-sky-700">
                      <ShieldCheck className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold text-slate-900">{r.full_name}</div>
                      <div className="font-mono text-[11px] text-slate-500">{r.username}</div>
                    </div>
                    {r.overdue_count > 0 ? (
                      <Badge className="bg-rose-600 hover:bg-rose-600 text-white">
                        <AlertTriangle className="mr-1 h-3 w-3" /> {r.overdue_count} overdue
                      </Badge>
                    ) : compliant ? (
                      <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white">
                        <CheckCircle2 className="mr-1 h-3 w-3" /> On schedule
                      </Badge>
                    ) : (
                      <Badge variant="secondary">No parks</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-slate-600">
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 font-medium">
                      <TreePine className="h-3 w-3" />{r.visited_in_month} / {r.assigned_count} visited
                    </span>
                    <div className="ml-auto h-1.5 w-24 rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${pct(r.visited_in_month, r.assigned_count)}%` }} />
                    </div>
                  </div>
                  <div className="rounded-md border bg-white">
                    {(r.parks || []).length === 0 ? (
                      <div className="p-3 text-center text-xs text-muted-foreground">No parks assigned</div>
                    ) : (
                      <>
                        {data?.week_days?.length ? (
                          <div className="border-b bg-slate-50 px-2 py-1 text-[10px] text-muted-foreground">
                            This week (Mon–Sun): <span className="font-medium text-slate-700">
                              {fmtDate(data.week_days[0])}
                            </span>
                            {" → "}
                            <span className="font-medium text-slate-700">
                              {fmtDate(data.week_days[data.week_days.length - 1])}
                            </span>
                          </div>
                        ) : null}
                        <ul className="divide-y divide-slate-100">
                          {r.parks.map((p) => (
                            <li key={p.park_id} className="flex flex-col gap-1 px-2 py-1.5 text-xs">
                              <div className="flex items-center gap-2">
                                <span className={`grid h-5 w-5 place-items-center rounded-full ${p.overdue ? "bg-rose-100 text-rose-700" : p.visits_this_week > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                                  {p.overdue ? <AlertTriangle className="h-3 w-3" /> : p.visits_this_week > 0 ? <CheckCircle2 className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                                </span>
                                <span className="font-mono text-[11px] font-semibold text-slate-800">{p.code}</span>
                                <span className="flex-1 truncate">{p.name}</span>
                                <span className={`tabular-nums text-[11px] ${p.overdue ? "text-rose-700 font-semibold" : "text-slate-500"}`}>
                                  {p.last_visit_at ? `${p.days_since}d ago` : "never"}
                                </span>
                              </div>
                              <div className="ml-7 flex items-center gap-1">
                                {(p.week_strip || []).map((v, i) => {
                                  const day = data?.week_days?.[i];
                                  const isToday = day === data?.today;
                                  const dayShort = day
                                    ? new Date(day + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", timeZone: "Asia/Kolkata" }).slice(0, 2)
                                    : "";
                                  return (
                                    <span
                                      key={i}
                                      className={`grid h-5 w-5 place-items-center rounded-full text-[9px] font-bold leading-none ${v ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"} ${isToday ? "ring-2 ring-amber-400" : ""}`}
                                      title={`${day || ""}${v ? " — visited" : " — not visited"}${isToday ? " (today)" : ""}`}
                                      data-testid={`sm-pv-week-${r.username}-${p.code}-${i}`}
                                    >{dayShort}</span>
                                  );
                                })}
                                <span className="ml-1.5 tabular-nums text-[10px] text-muted-foreground">
                                  {p.visits_this_week}/7
                                </span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ParkVisitsCalendarDialog
        open={!!calOpen}
        supervisorId={calOpen?.supervisor_id}
        supervisorName={calOpen?.full_name}
        username={calOpen?.username}
        month={month}
        onClose={() => setCalOpen(null)}
      />
    </div>
  );
}


/* -------------------------------------------------- ALL-PARKS CALENDAR */
const SUP_COLORS = [
  "bg-sky-500", "bg-emerald-500", "bg-amber-500", "bg-fuchsia-500",
  "bg-rose-500", "bg-indigo-500", "bg-teal-500", "bg-orange-500",
  "bg-cyan-500", "bg-lime-500", "bg-pink-500", "bg-violet-500",
];

function AllParksCalendarTab() {
  const [month, setMonth] = useState(currentMonthISO());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    setBusy(true);
    api
      .get("/admin/supervisor-monitoring/all-parks-calendar", { params: { month } })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], days: [], totals: {} }))
      .finally(() => setBusy(false));
  }, [month]);

  useEffect(() => { reload(); }, [reload]);

  const supColorBy = useMemo(() => {
    const m = {};
    const ids = new Set();
    (data?.rows || []).forEach((r) => r.cells.forEach((c) => c.supervisors.forEach((s) => ids.add(s.id))));
    Array.from(ids).sort().forEach((id, i) => { m[id] = SUP_COLORS[i % SUP_COLORS.length]; });
    return m;
  }, [data]);

  const supLegend = useMemo(() => {
    const out = [];
    const seen = new Set();
    (data?.rows || []).forEach((r) => r.cells.forEach((c) => c.supervisors.forEach((s) => {
      if (!seen.has(s.id)) { seen.add(s.id); out.push(s); }
    })));
    return out;
  }, [data]);

  const dayLabels = (data?.days || []).map((d) => d.split("-")[2]);
  const totals = data?.totals || { parks: 0, parks_visited: 0, total_visits: 0, parks_with_missed_weeks: 0 };
  const weeksMeta = useMemo(() => data?.weeks || [], [data]);

  // For each row, build the set of cell indices that fall inside a fully
  // elapsed Mon→Sun week where ZERO visits happened. These are the cells
  // we paint with a stronger rose tone + dashed ring.
  const missedIdxByRow = useMemo(() => {
    const map = {};
    (data?.rows || []).forEach((r) => {
      const set = new Set();
      (r.unvisited_weeks || []).forEach((wi) => {
        (weeksMeta[wi]?.indices || []).forEach((i) => set.add(i));
      });
      map[r.park_id] = set;
    });
    return map;
  }, [data, weeksMeta]);

  // Day indices that mark the START of a new Mon→Sun week (used to draw a
  // subtle vertical divider in the table — purely visual cue).
  const weekStartIdx = useMemo(() => {
    const s = new Set();
    weeksMeta.forEach((w, wi) => {
      // skip very first index (no left border needed before column 1)
      if (wi > 0 && w.indices?.length) s.add(w.indices[0]);
    });
    return s;
  }, [weeksMeta]);

  return (
    <div className="flex flex-col gap-4" data-testid="sm-all-parks">
      <Card>
        <CardContent className="grid grid-cols-1 items-end gap-4 p-4 md:grid-cols-12">
          <div className="md:col-span-4 flex flex-col gap-1.5">
            <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <CalendarDays className="mr-1 inline h-3 w-3" /> Month
            </Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10" data-testid="sm-apc-month" />
          </div>
          <div className="md:col-span-4 flex items-end">
            <Button variant="outline" onClick={reload} disabled={busy} className="h-10" data-testid="sm-apc-refresh">
              <RotateCw className={`mr-1 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <StatStrip cols={4}>
        <StatTile icon={TreePine} accent="emerald" label="Parks" value={totals.parks} testid="sm-apc-strip-parks" />
        <StatTile icon={CheckCircle2} accent="sky" label="Parks visited this month" value={totals.parks_visited} hint={`${pct(totals.parks_visited, totals.parks)}%`} testid="sm-apc-strip-vis" />
        <StatTile icon={AlertTriangle} accent="rose" label="Parks with missed weeks" value={totals.parks_with_missed_weeks || 0} hint={`${totals.elapsed_weeks || 0} elapsed wk(s)`} testid="sm-apc-strip-missed" />
        <StatTile icon={Activity} accent="amber" label="Total visits" value={totals.total_visits} testid="sm-apc-strip-tot" />
      </StatStrip>

      {supLegend.length > 0 && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 p-3 text-xs">
            <span className="text-muted-foreground">Supervisors:</span>
            {supLegend.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5" data-testid={`sm-apc-legend-${s.id}`}>
                <span className={`grid h-4 w-4 place-items-center rounded-full text-[9px] font-bold text-white ${supColorBy[s.id] || "bg-slate-400"}`}>{s.short}</span>
                {s.name}
              </span>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {busy ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : !data?.rows?.length ? (
            <div className="p-6 text-sm text-muted-foreground">No parks configured.</div>
          ) : (
            <table className="min-w-full text-xs" data-testid="sm-apc-table">
              <thead className="bg-card sticky top-0 z-10">
                <tr className="border-b">
                  <th className="sticky left-0 z-20 bg-card px-2 py-2 text-left font-medium">Park</th>
                  {dayLabels.map((d, i) => (
                    <th
                      key={i}
                      className={`px-1 py-2 text-center font-medium ${data.days[i] === data.today ? "bg-amber-100" : ""} ${weekStartIdx.has(i) ? "border-l-2 border-l-slate-500" : ""}`}
                    >{d}</th>
                  ))}
                  <th className="px-2 py-2 text-center font-medium border-l border-l-border bg-emerald-50/50">Days</th>
                  <th className="px-2 py-2 text-center font-medium bg-sky-50/50">Visits</th>
                  <th className="px-2 py-2 text-center font-medium bg-violet-50/50">Sups</th>
                  <th className="px-2 py-2 text-center font-medium bg-rose-50/60" title="Mon→Sun weeks that ended with zero visits">Wk&nbsp;miss</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const missedSet = missedIdxByRow[r.park_id] || new Set();
                  const rowMissed = (r.missed_week_count || 0) > 0;
                  return (
                  <tr
                    key={r.park_id}
                    className={`border-b last:border-0 hover:bg-amber-50/40 ${rowMissed ? "bg-rose-50/30" : ""}`}
                    data-testid={`sm-apc-row-${r.park_code}`}
                  >
                    <td className={`sticky left-0 z-10 bg-card px-2 py-1.5 ${rowMissed ? "border-l-2 border-l-rose-400" : ""}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[11px] font-semibold text-slate-800">{r.park_code}</span>
                        <span className="truncate">{r.park_name}</span>
                        {rowMissed ? (
                          <Badge className="ml-1 bg-rose-600 hover:bg-rose-600 text-white px-1.5 py-0 text-[10px] leading-tight" data-testid={`sm-apc-missed-badge-${r.park_code}`}>
                            <AlertTriangle className="mr-1 h-3 w-3" />{r.missed_week_count} wk
                          </Badge>
                        ) : null}
                      </div>
                    </td>
                    {r.cells.map((c, i) => {
                      const inMissed = missedSet.has(i);
                      const cls = c.is_future
                        ? "bg-slate-50 text-slate-300"
                        : c.visited
                          ? ""
                          : inMissed
                            ? "bg-rose-200/70 text-rose-700"
                            : "bg-rose-50/40 text-rose-300";
                      const tdExtra = `${c.is_today ? "ring-1 ring-amber-300 ring-inset" : ""} ${weekStartIdx.has(i) ? "border-l-2 border-l-slate-500" : ""} ${inMissed && !c.visited && !c.is_future ? "outline outline-1 outline-dashed outline-rose-400/60 -outline-offset-1" : ""}`;
                      return (
                        <td
                          key={c.date}
                          className={`relative px-0.5 py-0.5 text-center align-middle ${tdExtra}`}
                          title={`${c.date}${c.visited ? " · " + c.supervisors.map((s) => s.name).join(", ") : c.is_future ? "" : inMissed ? " · missed week (no visit Mon→Sun)" : " · no visit"}`}
                        >
                          {c.visited ? (
                            <div className="flex flex-wrap items-center justify-center gap-0.5" data-testid={`sm-apc-cell-${r.park_code}-${c.date}`}>
                              {c.supervisors.slice(0, 3).map((s) => (
                                <span
                                  key={s.id}
                                  className={`grid h-4 w-4 place-items-center rounded-full text-[8px] font-bold text-white ${supColorBy[s.id] || "bg-slate-400"}`}
                                >{s.short}</span>
                              ))}
                              {c.supervisors.length > 3 ? (
                                <span className="text-[9px] text-slate-500">+{c.supervisors.length - 3}</span>
                              ) : null}
                            </div>
                          ) : (
                            <span className={`block text-[10px] ${cls}`}>{c.is_future ? "·" : "—"}</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-center font-semibold text-emerald-700 border-l border-l-border bg-emerald-50/50">{r.visited_days}</td>
                    <td className="px-2 py-1.5 text-center font-semibold text-sky-700 bg-sky-50/50">{r.total_visits}</td>
                    <td className="px-2 py-1.5 text-center font-semibold text-violet-700 bg-violet-50/50">{r.unique_supervisors}</td>
                    <td className={`px-2 py-1.5 text-center font-semibold bg-rose-50/60 ${rowMissed ? "text-rose-700" : "text-slate-400"}`} data-testid={`sm-apc-missed-count-${r.park_code}`}>
                      {r.missed_week_count || 0}
                    </td>
                  </tr>
                );})}
              </tbody>
              <tfoot className="bg-slate-50 font-semibold">
                <tr>
                  <td className="sticky left-0 z-10 bg-slate-50 px-2 py-1.5">Total</td>
                  {dayLabels.map((_d, i) => {
                    const t = data.rows.reduce((acc, r) => acc + (r.cells[i]?.count || 0), 0);
                    return (
                      <td
                        key={i}
                        className={`px-1 py-1 text-center tabular-nums text-[11px] ${weekStartIdx.has(i) ? "border-l-2 border-l-slate-500" : ""}`}
                      >{t || ""}</td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-center text-emerald-800 border-l border-l-border bg-emerald-100/60">
                    {data.rows.reduce((acc, r) => acc + r.visited_days, 0)}
                  </td>
                  <td className="px-2 py-1.5 text-center text-sky-800 bg-sky-100/60">
                    {data.rows.reduce((acc, r) => acc + r.total_visits, 0)}
                  </td>
                  <td className="px-2 py-1.5 text-center text-violet-800 bg-violet-100/60">—</td>
                  <td className="px-2 py-1.5 text-center text-rose-800 bg-rose-100/60">
                    {data.rows.reduce((acc, r) => acc + (r.missed_week_count || 0), 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/* -------------------------------------------------------------- PAGE */
export default function SupervisorMonitoringPage() {
  const [tab, setTab] = useState("pmc");
  return (
    <AppShell
      title="Supervisor Monitoring"
      subtitle="Daily activity, expected vs actual captures, GPS trail and monthly park-visit compliance"
    >
      <Tabs value={tab} onValueChange={setTab} className="flex flex-col gap-4">
        <TabsList className="grid w-full grid-cols-4 md:w-[820px]" data-testid="sm-tabs">
          <TabsTrigger value="pmc" data-testid="sm-tab-pmc"><ShieldCheck className="mr-1 h-3.5 w-3.5" />ITCOT (PMC)</TabsTrigger>
          <TabsTrigger value="contractor" data-testid="sm-tab-contractor"><HardHat className="mr-1 h-3.5 w-3.5" />Contractor</TabsTrigger>
          <TabsTrigger value="visits" data-testid="sm-tab-visits"><CalendarCheck2 className="mr-1 h-3.5 w-3.5" />Park Visits</TabsTrigger>
          <TabsTrigger value="all-parks" data-testid="sm-tab-all-parks"><CalendarDays className="mr-1 h-3.5 w-3.5" />All Parks · Monthly</TabsTrigger>
        </TabsList>

        <TabsContent value="pmc" className="mt-0">
          <DailyMonitorTab role="pmc_supervisor" accent="sky" icon={ShieldCheck} />
        </TabsContent>
        <TabsContent value="contractor" className="mt-0">
          <DailyMonitorTab role="contractor_supervisor" accent="amber" icon={HardHat} />
        </TabsContent>
        <TabsContent value="visits" className="mt-0">
          <MonthlyParkVisitsTab />
        </TabsContent>
        <TabsContent value="all-parks" className="mt-0">
          <AllParksCalendarTab />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
